import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

import { DebugLoggerService } from '../../../services/debug-logger.service';
import { AppStateHolder } from '../../../shared';
import { PartyProfilerService } from './party-profiler.service';

@Component({
    selector: 'party-profiler',
    templateUrl: 'party-profiler.component.html',
    standalone: true,
    imports: [TooltipModule, UpperCasePipe, DatePipe, TranslateModule]
})

export class PartyProfilerComponent implements OnInit, OnDestroy {
  private service = inject(PartyProfilerService);
  private liveAnnouncer = inject(LiveAnnouncer);
  private translateService = inject(TranslateService);
  appStateHolder = inject(AppStateHolder);
  private _debugLogger = inject(DebugLoggerService);

  private destroy$ = new Subject<void>();

  @Input() modify: Boolean;
  @Input() idx: number;
  @Input() border: boolean;

  expiringCmp = [];
  companies: any[];
  widgetHeigth: String = '0px';
  status: string = 'ok';

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
  }

  ngOnInit(): void {
    this._debugLogger.logLifecycle('PartyProfiler', 'ngOnInit');
    if (!this.modify) {
      this.setStatus('busy');
      this.service
        .getUserCompanies()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          result => {
            this._debugLogger.logAction('PartyProfiler', 'companies loaded', { count: result?.length });
            this.appStateHolder.mapDevURL(result);
            this.companies = result;
            this.loadComptence(result);
          });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets the status and announces the change to screen readers
   * @param newStatus - The new status value ('ok' or 'busy')
   */
  private setStatus(newStatus: string): void {
    this.status = newStatus;

    // Announce status change to screen readers
    if (newStatus === 'busy') {
      this.announceToScreenReader('widgets.partyprofile-expiredcompetence.loading-announcement');
    }
  }

  /**
   * Announces a message to screen readers using LiveAnnouncer
   * @param translationKey - The i18n translation key for the message
   * @param params - Optional interpolation parameters
   */
  private announceToScreenReader(translationKey: string, params?: object): void {
    this.translateService.get(translationKey, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.liveAnnouncer.announce(message, 'polite');
      });
  }

  loadComptence(companies) {
    const requests = companies.map(r =>
      this.service.getPartProfiler(r.id)
    );

    if (requests.length === 0) {
      this.setStatus('ok');
      this.announceToScreenReader('widgets.partyprofile-expiredcompetence.no-expiring-competences');
      return;
    }

    // Track completed requests
    let completedCount = 0;

    companies.forEach((r, index) => {
      this.service.getPartProfiler(r.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          this._debugLogger.logAction('PartyProfiler', 'competence loaded', { company: r.name, count: res?.length });
          this.expiringCmp.push({ name: r.name, res: res });
          this.expiringCmp.sort((a: any, b: any): number => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0;
          });

          completedCount++;

          // Only announce completion when all requests are done
          if (completedCount === companies.length) {
            this.setStatus('ok');
            this.announceToScreenReader('widgets.partyprofile-expiredcompetence.data-loaded');
          }
        });
    });
  }

  haveCmp(): Boolean {

    if (this.expiringCmp === undefined)
      return false;

    return (this.expiringCmp.length > 0);
  }
}
